/**
 * Assign-crew picker for a PROJECT (A2, D-3) — Leo puts crew on the project
 * directly, no task needed. Same shape as the task picker (roster with each
 * member's load, multi-select, a lead marker), but the API materializes one
 * assignment block per crew member per committed day, so this dialog never
 * reasons about a single day's slot. Double-booking is WARN-not-block (D-11):
 * Leo stacks several jobs on one crew member in a day, so a member who's already
 * on other work is still selectable — the roster names that other work for the
 * load day, and the save's `warnings` (checked across every committed day)
 * surface as a heads-up toast.
 */
import { useMemo, useState } from 'react';
import { Loader2, Star, TriangleAlert, Users } from 'lucide-react';
import { toast } from 'sonner';
import type { ScheduleDay } from '@vari-crew/schemas';
import { useDispatchDay } from '@/api/dispatch';
import { useTasks } from '@/api/tasks';
import { useProjectAssignments, useSetProjectAssignments } from '@/api/project-assignments';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { cn } from '@/lib/cn';

export interface AssignProjectCrewButtonProps {
  project: {
    id: string;
    title: string;
    approved_schedule: ScheduleDay[] | null;
  };
  /** Render as a full button (card header) or the small inline link style. */
  variant?: 'button' | 'link';
}

function todayLocal(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

export function AssignProjectCrewButton({ project, variant = 'button' }: AssignProjectCrewButtonProps) {
  const [open, setOpen] = useState(false);
  return (
    <>
      {variant === 'button' ? (
        <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
          <Users className="w-3.5 h-3.5" />
          Assign crew
        </Button>
      ) : (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setOpen(true);
          }}
          className="inline-flex items-center gap-1 text-[11px] text-ink-faint hover:text-accent shrink-0"
        >
          <Users className="w-3.5 h-3.5" />
          Assign crew
        </button>
      )}
      {open && <AssignProjectCrewDialog project={project} onClose={() => setOpen(false)} />}
    </>
  );
}

/** Mounted only while open, so the roster + current-crew fetches happen on demand. */
export function AssignProjectCrewDialog({
  project,
  onClose,
}: {
  project: AssignProjectCrewButtonProps['project'];
  onClose: () => void;
}) {
  const days = useMemo(
    () => [...(project.approved_schedule ?? [])].filter((d) => d?.date).sort((a, b) => a.date.localeCompare(b.date)),
    [project.approved_schedule],
  );
  // Roster + load come from the dispatch day: the first committed day when
  // there is one (that's the day the load matters for), else today.
  const loadDay = days[0]?.date ?? todayLocal();
  const dispatch = useDispatchDay(loadDay);
  const current = useProjectAssignments(project.id);
  const set = useSetProjectAssignments(project.id);
  const rows = dispatch.data?.crew_rows ?? [];
  // Assigning project-level crew on a project that has tasks makes it a
  // HYBRID: the crew are held every committed day and the project becomes
  // directly steered (task rollup stops moving its status). Legitimate, but
  // easy to do by accident — say so (Ben 2026-08-28, calendar-duplication
  // report). Query is cached from ProjectDetail, so this is free.
  const { data: projTasks } = useTasks({ project_id: project.id, limit: 50, offset: 0 });
  const liveTaskCount = (projTasks?.data ?? []).filter((t) => t.status !== 'cancelled').length;

  const loaded = useMemo(() => {
    const sel = new Set<string>();
    let lead: string | null = null;
    for (const a of current.data?.assignments ?? []) {
      // Step-inherited crew (via_tasks) aren't project-assigned — the picker
      // manages only the project-level set. Pre-selecting them here would
      // silently promote every step crew member on the next Save.
      if (a.via_tasks) continue;
      sel.add(a.crew_member_id);
      if (a.is_lead) lead = a.crew_member_id;
    }
    return { sel, lead };
  }, [current.data]);

  // Local edits override the loaded state once the user touches anything.
  const [override, setOverride] = useState<{ sel: Set<string>; lead: string | null } | null>(null);
  const view = override ?? loaded;

  function toggle(cid: string) {
    const base = override ?? loaded;
    const sel = new Set(base.sel);
    let lead = base.lead;
    if (sel.has(cid)) {
      sel.delete(cid);
      // Keep a lead designated whenever crew remain (VCS-401).
      if (lead === cid) lead = sel.size > 0 ? ([...sel][0] ?? null) : null;
    } else {
      sel.add(cid);
      if (!lead) lead = cid; // first picked defaults to lead
    }
    setOverride({ sel, lead });
  }

  function makeLead(cid: string) {
    const base = override ?? loaded;
    if (!base.sel.has(cid)) return;
    setOverride({ sel: new Set(base.sel), lead: cid });
  }

  function save() {
    set.mutate(
      { assignments: [...view.sel].map((cid) => ({ crew_member_id: cid, is_lead: cid === view.lead })) },
      {
        onSuccess: (data) => {
          const base = view.sel.size === 0 ? 'Crew cleared' : `Crew assigned (${view.sel.size})`;
          const warnings = data.warnings ?? [];
          if (warnings.length > 0) {
            toast.warning(`${base} — heads up: ${warnings.map((w) => `${w.name} also on ${w.label}`).join('; ')}`);
          } else {
            toast.success(base);
          }
          onClose();
        },
        onError: (err) => toast.error(`Assign failed: ${err.message}`),
      },
    );
  }

  // What else each crew member is on during the load day (other projects,
  // their tasks, pickups) — shown as a heads-up, never a block (D-11).
  const otherWork = useMemo(() => {
    const byCrew = new Map<string, string[]>();
    for (const row of rows) {
      const labels = new Set<string>();
      for (const pb of row.project_blocks) {
        if (pb.project_id !== project.id) labels.add(pb.project_title);
      }
      for (const a of row.assignments) {
        const t = dispatch.data?.tasks_by_id?.[a.task_id];
        if (t && t.project_id !== project.id) labels.add(t.project_title);
      }
      for (const pk of row.pickup_blocks) labels.add(pk.title);
      if (labels.size > 0) byCrew.set(row.crew_member.id, [...labels]);
    }
    return byCrew;
  }, [rows, dispatch.data?.tasks_by_id, project.id]);

  const dayLabel = (d: string) =>
    new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });

  return (
    <Dialog
      open
      onClose={onClose}
      busy={set.isPending}
      title={
        <>
          <Users className="w-4 h-4" />
          Assign crew to project
        </>
      }
    >
      <div className="p-4 space-y-3">
        <div>
          <div className="font-medium text-sm truncate">{project.title}</div>
          <div className="text-[11px] text-ink-faint">
            {view.sel.size} selected ·{' '}
            {days.length > 0
              ? `${days.length} committed ${days.length === 1 ? 'day' : 'days'} (${dayLabel(days[0]!.date)}${
                  days.length > 1 ? ` – ${dayLabel(days[days.length - 1]!.date)}` : ''
                }) · load shown for ${dayLabel(loadDay)}`
              : 'no committed dates yet — crew stay assigned and get their days once the schedule is approved'}
          </div>
        </div>

        {liveTaskCount > 0 && (
          <div className="rounded-md border border-line bg-fill-soft px-2.5 py-1.5 text-[11px] text-ink-dim">
            This project also has {liveTaskCount} task{liveTaskCount === 1 ? '' : 's'}. Crew
            assigned here are held at the <span className="font-medium">project level</span> for
            every committed day — separate from task assignments — and the project&apos;s status is
            then driven directly rather than by its tasks. Calendars show the task where one
            covers a day.
          </div>
        )}

        {dispatch.isLoading || current.isLoading ? (
          <div className="text-xs text-ink-faint py-4 text-center">Loading crew…</div>
        ) : rows.length === 0 ? (
          <div className="text-xs text-ink-faint py-4 text-center">No active crew.</div>
        ) : (
          <ul className="space-y-1 max-h-72 overflow-auto">
            {rows.map((row) => {
              const cm = row.crew_member;
              const selected = view.sel.has(cm.id);
              const isLead = view.lead === cm.id;
              const alsoOn = otherWork.get(cm.id);
              const pct = row.capacity_hours > 0 ? Math.round((row.assigned_hours / row.capacity_hours) * 100) : 0;
              return (
                <li
                  key={cm.id}
                  className={cn(
                    'flex items-center gap-2 rounded-md border px-2 py-1.5',
                    selected ? 'border-accent bg-accent/5' : 'border-line',
                  )}
                >
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => toggle(cm.id)}
                    aria-label={`Assign ${cm.user.display_name}`}
                    className="shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm truncate">
                      {cm.user.display_name}
                      <span className="text-ink-faint"> · {cm.crew_role}</span>
                    </div>
                    <div className="text-[11px] flex items-center gap-1.5 min-w-0">
                      <span
                        className={cn(
                          pct > 100 ? 'text-status-blocked-fg' : pct >= 80 ? 'text-amber-700' : 'text-ink-faint',
                        )}
                      >
                        {row.assigned_hours}h / {row.capacity_hours}h ({pct}%)
                      </span>
                      {alsoOn ? (
                        <span
                          className="inline-flex items-center gap-0.5 text-amber-700 min-w-0"
                          title={`Also on ${alsoOn.join(', ')} that day`}
                        >
                          <TriangleAlert className="w-3 h-3 shrink-0" />
                          <span className="truncate">also on {alsoOn.join(', ')}</span>
                        </span>
                      ) : row.has_conflict ? (
                        <span className="inline-flex items-center gap-0.5 text-status-blocked-fg">
                          <TriangleAlert className="w-3 h-3" /> conflict
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <button
                    type="button"
                    disabled={!selected}
                    onClick={() => makeLead(cm.id)}
                    title={isLead ? 'Lead' : 'Make lead'}
                    className={cn(
                      'shrink-0 inline-flex items-center gap-0.5 text-[11px] px-1.5 py-0.5 rounded',
                      isLead ? 'text-amber-700 font-medium' : 'text-ink-faint hover:text-ink disabled:opacity-30',
                    )}
                  >
                    <Star className={cn('w-3.5 h-3.5', isLead && 'fill-amber-400')} />
                    Lead
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {view.sel.size > 0 && !view.lead && (
          <p className="text-[11px] text-amber-700">Mark one crew member as the lead before saving.</p>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={onClose} disabled={set.isPending}>
            Cancel
          </Button>
          <Button size="sm" onClick={save} disabled={set.isPending || (view.sel.size > 0 && !view.lead)}>
            {set.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Save
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
